import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Linkedin, Users, Building2 } from "lucide-react";
import api from "@/lib/api";

const RED = "#D62B3A";
const GREY = "#6F6F6F";

const FILTERS = [
  { id: "alle", label: "Alle" },
  { id: "fuehrung", label: "Führung" },
  { id: "zentrale", label: "Zentrale Funktionen" },
  { id: "standort", label: "Standorte" },
];

function initials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");
}

const Avatar = ({ m }) =>
  m.photo_url ? (
    <img
      src={m.photo_url}
      alt={`${m.full_name}, ${(m.role_titles || []).join(" · ")} bei HELIUM Catering`}
      className="w-20 h-20 rounded-full object-cover"
    />
  ) : (
    <div
      className="w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-bold"
      style={{ backgroundColor: RED }}
      aria-label={`${m.full_name}, ${(m.role_titles || []).join(" · ")} bei HELIUM Catering`}
      data-testid="team-avatar-initials"
    >
      {initials(m.full_name) || "H"}
    </div>
  );

const MemberCard = ({ m }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-5 flex gap-4"
    data-testid={`team-member-${m.id}`}
  >
    <Avatar m={m} />
    <div className="min-w-0 flex-1">
      <div className="font-bold text-gray-900 leading-tight">{m.full_name}</div>
      <div className="text-sm font-medium mt-0.5" style={{ color: RED }}>
        {(m.role_titles || []).join(" · ")}
      </div>
      {m.short_bio && <p className="mt-2 text-sm text-gray-600 leading-relaxed line-clamp-4">{m.short_bio}</p>}
      <div className="mt-2 flex items-center gap-3 text-xs" style={{ color: GREY }}>
        {m.start_date && <span>Teil des Teams seit {new Date(m.start_date).getFullYear()}</span>}
        {m.linkedin_url && (
          <a href={m.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#D62B3A]" data-testid={`team-linkedin-${m.id}`}>
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
          </a>
        )}
      </div>
    </div>
  </div>
);

const BubbleDivider = () => (
  <div className="flex items-center justify-center gap-2 my-12" aria-hidden="true">
    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: RED }} />
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: GREY }} />
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: RED, opacity: 0.6 }} />
  </div>
);

const SectionHead = ({ eyebrow, title }) => (
  <div className="text-center max-w-2xl mx-auto mb-8">
    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: RED }}>{eyebrow}</span>
    <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold font-display text-gray-900">{title}</h3>
  </div>
);

export default function TeamSection() {
  const [data, setData] = useState({ members: [], branch_teams: [], roles: [] });
  const [branches, setBranches] = useState([]);
  const [filter, setFilter] = useState("alle");

  useEffect(() => {
    api.get("/team/public").then((r) => setData(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const fuehrung = useMemo(() => data.members.filter((m) => m.section === "fuehrung"), [data]);
  const zentrale = useMemo(() => data.members.filter((m) => m.section === "zentrale"), [data]);
  const standortMembers = useMemo(() => data.members.filter((m) => m.section === "standort"), [data]);
  const btByBranch = useMemo(() => {
    const map = {};
    (data.branch_teams || []).forEach((bt) => (map[bt.branch_id] = bt));
    return map;
  }, [data]);

  const branchBlocks = useMemo(
    () =>
      branches
        .map((b) => ({ branch: b, bt: btByBranch[b.id], members: standortMembers.filter((m) => m.branch_id === b.id) }))
        .filter((x) => (x.bt && x.bt.is_visible) || x.members.length > 0),
    [branches, btByBranch, standortMembers]
  );

  const show = (id) => filter === "alle" || filter === id;
  const nothing = fuehrung.length === 0 && zentrale.length === 0 && branchBlocks.length === 0;

  return (
    <section id="team" className="scroll-mt-24 bg-[#F9F8F6]" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full" style={{ backgroundColor: RED }}>Unser Team</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold font-display text-gray-900">Die Menschen hinter HELIUM</h2>
          <p className="mt-4 text-gray-600 leading-relaxed">
            HELIUM lebt von den Menschen, die jeden Tag mit Herz dabei sind — in der Führung, in der Verwaltung und an jedem einzelnen Standort. Lernen Sie uns kennen.
          </p>
        </div>

        {/* Filter chips */}
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`team-filter-${f.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${filter === f.id ? "text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
              style={filter === f.id ? { backgroundColor: RED } : {}}
            >
              {f.label}
            </button>
          ))}
        </div>

        {nothing && (
          <div className="mt-12 text-center text-gray-500" data-testid="team-empty">
            <Users className="w-10 h-10 mx-auto mb-3" style={{ color: GREY }} />
            Unser Team wird in Kürze vorgestellt.
          </div>
        )}

        {/* Führung */}
        {show("fuehrung") && fuehrung.length > 0 && (
          <div className="mt-14" data-testid="team-group-fuehrung">
            <SectionHead eyebrow="Führung" title="Unsere Geschäftsführung" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {fuehrung.map((m) => <MemberCard key={m.id} m={m} />)}
            </div>
          </div>
        )}

        {show("fuehrung") && show("zentrale") && fuehrung.length > 0 && zentrale.length > 0 && <BubbleDivider />}

        {/* Zentrale Funktionen */}
        {show("zentrale") && zentrale.length > 0 && (
          <div className="mt-6" data-testid="team-group-zentrale">
            <SectionHead eyebrow="Zentrale Funktionen" title="Verwaltung & zentrale Bereiche" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {zentrale.map((m) => <MemberCard key={m.id} m={m} />)}
            </div>
          </div>
        )}

        {show("standort") && (fuehrung.length > 0 || zentrale.length > 0) && branchBlocks.length > 0 && <BubbleDivider />}

        {/* Teams vor Ort */}
        {show("standort") && branchBlocks.length > 0 && (
          <div className="mt-6" data-testid="team-group-standort">
            <SectionHead eyebrow="Teams vor Ort" title="Unsere Standort-Teams" />
            <div className="space-y-10">
              {branchBlocks.map(({ branch, bt, members }) => (
                <div key={branch.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`branch-team-${branch.slug}`}>
                  <div className="grid md:grid-cols-2">
                    {bt && bt.group_photo_url ? (
                      <img src={bt.group_photo_url} alt={`Team der HELIUM Cafeteria ${branch.name}`} className="w-full h-56 md:h-full object-cover" />
                    ) : (
                      <div className="w-full h-56 md:h-full min-h-[200px] flex flex-col items-center justify-center gap-2 text-white" style={{ backgroundColor: GREY }} aria-label={`Team der HELIUM Cafeteria ${branch.name}`}>
                        <Building2 className="w-12 h-12 opacity-80" />
                        <span className="text-sm opacity-90">Gruppenfoto folgt</span>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="text-xs font-bold uppercase tracking-widest" style={{ color: RED }}>{branch.name}</div>
                      {bt && bt.team_description && <p className="mt-2 text-gray-700 leading-relaxed">{bt.team_description}</p>}
                      {bt && bt.team_size_display && <div className="mt-2 text-sm font-semibold" style={{ color: GREY }}>{bt.team_size_display}</div>}
                      {members.length > 0 && (
                        <div className="mt-5 grid sm:grid-cols-1 gap-4">
                          {members.map((m) => <MemberCard key={m.id} m={m} />)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
